import { useSetToast } from "@app/components/Toast";
import TagListEditor from "@app/shared/components/TagListEditor";
import { useOptimistic } from "@app/shared/utils/optimisticUpdates";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { ResponseStatus, TagType } from "@generated/client/graphql";
import { useMemo, useState, type FC } from "react";
import { useParams } from "react-router-dom";
import { useSupplier, useSupplierIcon, useUpdateSupplier } from "../../data";
import { EditSupplierModal } from "../EditSupplierModal";
import { Header } from "./Header";
import { useSupplierHeaderTranslations } from "./i18n";

export const SupplierHeader: FC = () => {
    const { supplier_id } = useParams();
    const { supplier, isPending } = useSupplier(supplier_id);

    const { t, keys } = useSupplierHeaderTranslations();

    const { updateSupplier } = useUpdateSupplier();
    const tagIds = useMemo(
        () => supplier?.tags?.map((tag) => tag.id),
        [supplier],
    );

    const [isEditSupplierModalOpen, setIsEditSupplierModalOpen] =
        useState(false);

    const [optimisticTagIds, setOptimisticTagIds] = useOptimistic(tagIds);
    const [supplierName, setSupplierName] = useOptimistic(supplier?.name);

    const { setToastError, setToastSuccess } = useSetToast();

    const handleTagChange = (tagIds: string[]) => {
        if (!supplier_id) return;
        setOptimisticTagIds(tagIds);
        updateSupplier(
            {
                id: supplier_id,
                set: {
                    tagIds,
                },
            },
            {
                onSettled(data, error) {
                    if (
                        error ||
                        data?.updateSupplier?.status === ResponseStatus.Error
                    ) {
                        setToastError(t(keys.toast_error_update_supplier));
                    }
                },
            },
        );
    };

    const handleEditSupplier = () => {
        setIsEditSupplierModalOpen(true);
    };

    const handleEditSupplierModalSave = () => {
        if (!supplier_id) return;
        updateSupplier(
            {
                id: supplier_id,
                set: {
                    name: supplierName,
                },
            },
            {
                onSettled(data, error) {
                    if (
                        error ||
                        data?.updateSupplier?.status === ResponseStatus.Error
                    ) {
                        setToastError(t(keys.toast_error_update_supplier));
                        return;
                    }
                    setToastSuccess(t(keys.toast_success_update_supplier));
                },
            },
        );
    };

    const handleEditSupplierModalOpenChange = (open: boolean) => {
        setIsEditSupplierModalOpen(open);
        if (!open) {
            setSupplierName(supplier?.name);
        }
    };

    const { iconUrl, isPending: isIconPending } = useSupplierIcon(
        supplier_id,
        supplier?.name,
    );

    return (
        <Header>
            {isPending ? (
                <>
                    <Header.LogoSkeleton />
                    <FlexCol gap="3">
                        <Header.TitleSkeleton />
                        <Header.SubtitleSkeleton />
                    </FlexCol>
                </>
            ) : (
                <>
                    {isIconPending ? (
                        <Header.LogoSkeleton />
                    ) : (
                        <Header.Logo
                            imageUrl={iconUrl}
                            letter={supplierName?.[0] ?? ""}
                        />
                    )}
                    <FlexCol gap="3" w="full">
                        <FlexRow alignItems="center" justifyContent="between">
                            <Header.Title>{supplierName}</Header.Title>
                            <Header.EditButton onClick={handleEditSupplier}>
                                {t(keys.edit_btn)}
                            </Header.EditButton>
                        </FlexRow>
                        <Header.Subtitle>{t(keys.subtitle)}</Header.Subtitle>
                        <Box w="1/2">
                            <TagListEditor
                                values={optimisticTagIds}
                                onValuesChange={handleTagChange}
                                tagType={TagType.Supplier}
                            />
                        </Box>
                    </FlexCol>
                    <EditSupplierModal
                        open={isEditSupplierModalOpen}
                        onOpenChange={handleEditSupplierModalOpenChange}
                        supplierName={supplierName ?? ""}
                        onSupplierNameChange={setSupplierName}
                        onSave={handleEditSupplierModalSave}
                    />
                </>
            )}
        </Header>
    );
};
